import { useState, useRef } from 'react'
import {
  Spinner, Alert, Button, Card, Table, Badge
} from 'react-bootstrap'
import client from '../api/client'

function Files() {
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef(null)

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('fr-FR')
  }

  const handleFileUpload = async (file) => {
    if (!file) return

    setUploading(true)
    setError('')

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await client.post('/api/v1/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      const newFile = {
        ...response.data,
        uploadedAt: Date.now()
      }

      setUploadedFiles(prev => [newFile, ...prev])
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'upload')
      console.error(err)
    } finally {
      setUploading(false)
    }
  }

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileUpload(file)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const file = e.dataTransfer?.files?.[0]
    if (file) {
      handleFileUpload(file)
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div>
      <h2 className="mb-4">Gestion des Fichiers</h2>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <div
            className={`border rounded p-5 text-center ${dragActive ? 'border-primary bg-light' : 'border-secondary'}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
            onClick={handleButtonClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt,.csv,.md"
            />
            {uploading ? (
              <div>
                <Spinner animation="border" className="mb-2" />
                <p className="text-muted mb-0">Upload en cours...</p>
              </div>
            ) : (
              <div>
                <i className="bi bi-cloud-upload" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3">Glissez un fichier ici ou cliquez pour parcourir</h5>
                <p className="text-muted mb-0">
                  Formats acceptés: PDF, DOC, DOCX, TXT, CSV, MD (max 10MB)
                </p>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {uploadedFiles.length > 0 && (
        <Card>
          <Card.Header>
            <h5 className="mb-0">Fichiers Uploadés ({uploadedFiles.length})</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Taille</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {uploadedFiles.map((file, index) => (
                  <tr key={index}>
                    <td>{file.filename}</td>
                    <td>{formatFileSize(file.size)}</td>
                    <td>
                      <Badge bg="secondary">{file.content_type}</Badge>
                    </td>
                    <td>{formatDate(file.uploadedAt)}</td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="bi bi-download"></i> Télécharger
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {uploadedFiles.length === 0 && !uploading && (
        <Alert variant="info">
          Aucun fichier uploadé. Utilisez la zone ci-dessus pour uploader des fichiers.
        </Alert>
      )}
    </div>
  )
}

export default Files
